import { Icon, Image, LoadingShimmer, PlayFilledSVG } from "@newsela/angelou";
import dayjs from "dayjs";

import BookmarkButton from "@/components/BookmarkButton";
import VISIBILITY_MARKER_REASONS from "@/constants/VISIBILITY_MARKER_REASONS";
import { ArticleStoryTypesEnum } from "@/home/TeacherHomepage/types/ArticleStoryTypesEnum";
import { DEFAULT_PAGE } from "@/search/NewSearchPageMVP/NewSearchPageMVP.constants";
import { useSearchPageContext } from "@/search/NewSearchPageMVP/provider/SearchPageProvider";
import {
  ARTICLE_STORY_MAP,
  CONTENT_CARD_READ_UNTIL_DATE_TIME_FORMAT,
  CONTENT_TYPES_LABELS,
  ContentTypesEnum,
} from "@/search/NewSearchPageMVP/SearchResults/ContentCard/ContentCard.constants";
import {
  advisoryFlaggedTagCss,
  articleTypeLabelCss,
  breakingNewsTagCss,
  contentCardImageCss,
  contentCardImagesContainerCss,
  contentCardTopSectionContainerCss,
  flaggedContainerCss,
  playIconCss,
  premiumTagCss,
  readUntilTagCss,
  videoDurationTagCss,
} from "@/search/NewSearchPageMVP/SearchResults/ContentCard/ContentCard.styles";
import { standardBookmarkButtonCss } from "@/search/NewSearchPageMVP/SearchResults/StandardCard/StandardCard.styles";
import { AgathaSchemaParamsEnum } from "@/search/NewSearchPageMVP/types/IParametersSchema";
import { ISearchResult } from "@/search/NewSearchPageMVP/types/ISearchResult";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";
import convertToImgixUrl from "@/utils/convertToImgixUrl";

export const getFormattedVideoDuration = (lengthInSeconds = 0) => {
  return (
    `${Math.floor(lengthInSeconds / 60)}:` +
    `0${lengthInSeconds % 60}`.slice(-2)
  );
};

const ContentCardTopSection = ({
  content,
  isBreakingNews,
  showContentType,
}: IContentCardTopSection) => {
  const { data: user } = useUser();
  const { isTeacher, isLiteUser } = getUserProperties(user, {
    shouldUseNewUserApi: false,
  });

  const {
    getters: { isSearchLoading },
    handlers: { hasSearchParamValue },
  } = useSearchPageContext();

  const {
    images = [],
    content_id: contentId,
    story_type: storyType,
    content_type: contentType,
    length_in_seconds: lengthInSeconds,
    visibility_markers: visibilityMarkers = [],
    date_lite_product_grant_expires: dateLiteProductGrantExpires,
  } = content;

  const isVideo = !!lengthInSeconds;
  const videoDuration = getFormattedVideoDuration(lengthInSeconds);

  const currentDate = dayjs();
  const dateLiteProductGrantExpiresFormatted = dayjs(
    dateLiteProductGrantExpires,
  );
  const contentHasExpired = currentDate > dateLiteProductGrantExpiresFormatted;

  const isFreeContent =
    isLiteUser && dateLiteProductGrantExpires && !contentHasExpired;
  const showPremiumTag =
    (isLiteUser && (!dateLiteProductGrantExpires || contentHasExpired)) ||
    (isTeacher &&
      (!dateLiteProductGrantExpires || contentHasExpired) &&
      visibilityMarkers.find(
        (marker) => marker.reason === VISIBILITY_MARKER_REASONS.PREMIUM_CONTENT,
      ));

  const showAdvisoryTag =
    isTeacher &&
    visibilityMarkers.find(
      (marker) =>
        marker.reason === VISIBILITY_MARKER_REASONS.DISTRICT_ADVISORY ||
        marker.reason === VISIBILITY_MARKER_REASONS.SCHOOL_ADVISORY,
    );
  const showFlaggedTag =
    !isTeacher &&
    visibilityMarkers.find((marker) => !!marker?.flagged_at_my_admin_level);

  const storyTypeLabel = ARTICLE_STORY_MAP[storyType as ArticleStoryTypesEnum];
  const contentTypeLabel =
    CONTENT_TYPES_LABELS[contentType as ContentTypesEnum];

  const isViewingFirstPage = hasSearchParamValue(
    AgathaSchemaParamsEnum.page,
    DEFAULT_PAGE,
  );

  return (
    <div data-testid="content-card-top" css={contentCardTopSectionContainerCss}>
      <div css={contentCardImagesContainerCss}>
        {isSearchLoading && isViewingFirstPage ? (
          <LoadingShimmer />
        ) : (
          <>
            {images.slice(0, 3).map((image, index) => (
              <Image
                key={index}
                loading="lazy"
                lang={image.lang || ""}
                alt={image.alt_text || ""}
                src={convertToImgixUrl(image.formatted_url || "")}
                __css={contentCardImageCss}
              />
            ))}
          </>
        )}
      </div>

      <div css={flaggedContainerCss}>
        {isFreeContent && (
          <time dateTime={dateLiteProductGrantExpires} css={readUntilTagCss}>
            Read until{" "}
            {dayjs(dateLiteProductGrantExpires).format(
              CONTENT_CARD_READ_UNTIL_DATE_TIME_FORMAT,
            )}
          </time>
        )}
        {showPremiumTag && <div css={premiumTagCss}>Premium</div>}
        {showAdvisoryTag && <div css={advisoryFlaggedTagCss}>Advisory</div>}
        {showFlaggedTag && <div css={advisoryFlaggedTagCss}>Flagged</div>}
      </div>

      {isVideo && (
        <div css={videoDurationTagCss}>
          <Icon SvgComponent={PlayFilledSVG} __cssFor={playIconCss} />
          {videoDuration}
        </div>
      )}

      {isBreakingNews && <div css={breakingNewsTagCss}>BREAKING NEWS</div>}
      {!isBreakingNews && showContentType && (
        <div css={articleTypeLabelCss}>
          {storyTypeLabel || contentTypeLabel}
        </div>
      )}

      <BookmarkButton
        contentId={contentId}
        __cssFor={standardBookmarkButtonCss}
      />
    </div>
  );
};

interface IContentCardTopSection {
  content: ISearchResult;
  showContentType?: boolean;
  isBreakingNews?: boolean | undefined;
}

export { ContentCardTopSection };
